import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Page, PageHeader, Section } from '../../../components/layout/primitives';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import Modal from '../../../components/ui/Modal';
import { getUser } from '../../../lib/storage';
import {
  mcpService,
  type CreateMcpServerPayload,
  type McpMasterServer,
} from '../../../services/mcpService';
import { UserRoleEnums } from '../../../utility/userRoles';
import { Checkbox, SelectField, TextArea } from '../../../components/ui/Field';
import { Table } from '../../../components/ui/Table';

type ServerFormState = {
  code: string;
  name: string;
  description: string;
  transportType: string;
  endpoint: string;
  authScheme: string;
  metadata: string;
  status: string;
  isActive: boolean;
  allowApproveWithoutTools: boolean;
};

const defaultFormState: ServerFormState = {
  code: '',
  name: '',
  description: '',
  transportType: 'streamable_http',
  endpoint: '',
  authScheme: 'bearer',
  metadata: '{}',
  status: 'draft',
  isActive: true,
  allowApproveWithoutTools: false,
};

const MCP_STATUSES = ['draft', 'approved', 'deprecated'];
const MCP_TRANSPORTS = ['streamable_http', 'adapter_http', 'sse', 'stdio'];
const MCP_AUTH_SCHEMES = ['bearer', 'oauth2', 'api_key', 'basic', 'none', 'custom'];
const MCP_TRANSPORT_LABELS: Record<string, string> = {
  streamable_http: 'MCP over HTTP (Streamable)',
  adapter_http: 'Internal Adapter (HTTP)',
  sse: 'Server-Sent Events (SSE)',
  stdio: 'Local Process (STDIO)',
};
const MCP_AUTH_LABELS: Record<string, string> = {
  bearer: 'Bearer Token',
  oauth2: 'OAuth 2.0',
  api_key: 'API Key',
  basic: 'Basic Auth',
  none: 'No Authentication',
  custom: 'Custom',
};

const generateMcpCode = (name: string) => {
  const normalized = (name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

  return (normalized || 'mcp_master').slice(0, 60);
};

const McpCatalogPage: React.FC = () => {
  const user = getUser();
  const role = user?.user?.role;

  const [items, setItems] = useState<McpMasterServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | string>('all');

  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState<McpMasterServer | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ServerFormState>(defaultFormState);

  const [actionLoading, setActionLoading] = useState<Record<string, string>>({});
  const [toolCountByServer, setToolCountByServer] = useState<Record<string, number>>({});
  const [lastMessageByServer, setLastMessageByServer] = useState<Record<string, string>>({});

  const setServerMessage = (serverId: string, message: string) => {
    setLastMessageByServer((current) => ({
      ...current,
      [serverId]: message,
    }));
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await mcpService.listMasterServers({
        limit: 100,
        search: search.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      const servers = res?.items || [];
      setItems(servers);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to load MCP masters');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const loadToolCount = useCallback(async (serverId: string) => {
    try {
      const toolRes = await mcpService.listMasterServerTools(serverId, { page: 1, limit: 1 });
      const total = Number(toolRes?.total || 0);
      setToolCountByServer((current) => ({ ...current, [serverId]: total }));
      return total;
    } catch (e) {
      setToolCountByServer((current) => ({ ...current, [serverId]: 0 }));
      return 0;
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      load();
    }, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!items.length) return;
    items.forEach((server) => {
      if (toolCountByServer[server.id] === undefined) {
        loadToolCount(server.id);
      }
    });
  }, [items, toolCountByServer, loadToolCount]);

  const resetForm = () => {
    setForm(defaultFormState);
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setOpenModal(true);
  };

  const openEdit = (server: McpMasterServer) => {
    const metadataString = server.metadata && typeof server.metadata === 'object'
      ? JSON.stringify(server.metadata, null, 2)
      : '{}';
    setEditing(server);
    setForm({
      code: server.code || '',
      name: server.name || '',
      description: server.description || '',
      transportType: server.transportType || 'streamable_http',
      endpoint: server.endpoint || '',
      authScheme: server.authScheme || 'bearer',
      metadata: metadataString,
      status: server.status || 'draft',
      isActive: server.isActive,
      allowApproveWithoutTools: false,
    });
    setOpenModal(true);
  };

  const updateForm = (patch: Partial<ServerFormState>) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const updateName = (value: string) => {
    if (editing) {
      updateForm({ name: value });
      return;
    }
    const lower = value.toLowerCase();
    const isCalendly = lower.includes('calendly');
    updateForm({
      name: value,
      code: generateMcpCode(value),
      ...(isCalendly
        ? {
            transportType: 'streamable_http',
            authScheme: 'oauth2',
            endpoint: 'https://mcp.calendly.com/',
          }
        : {}),
    });
  };

  const isStdioTransport = form.transportType === 'stdio';
  const endpointLabel = form.transportType === 'adapter_http' ? 'Adapter Base URL' : 'MCP Endpoint URL';
  const endpointPlaceholder =
    form.transportType === 'adapter_http'
      ? 'http://mcp-adapter:8080/providers/hubspot/mcp'
      : 'https://example.com/mcp';
  const endpointHelper =
    form.transportType === 'adapter_http'
      ? 'For adapter mode, use your internal adapter URL (not the provider API URL).'
      : form.transportType === 'streamable_http'
        ? 'URL of the MCP server supporting streamable HTTP.'
        : form.transportType === 'sse'
          ? 'URL of the MCP server SSE endpoint.'
          : 'Stdio transport runs local process-based integrations, so URL is not required.';
  const selectedAuthScheme = MCP_AUTH_SCHEMES.includes((form.authScheme || '').toLowerCase())
    ? (form.authScheme || '').toLowerCase()
    : 'custom';

  const checkApprovalGuard = async (serverId?: string) => {
    if (form.status !== 'approved') return true;
    if (!serverId) {
      toast.error('Create the server as draft, run discovery, then set status to approved.');
      return false;
    }

    const count = await loadToolCount(serverId);
    if (count > 0) return true;
    if (form.allowApproveWithoutTools) return true;

    toast.error('Cannot set approved with zero discovered tools. Tick override to continue.');
    return false;
  };

  const saveServer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('Code and Name are required');
      return;
    }

    const canProceed = await checkApprovalGuard(editing?.id);
    if (!canProceed) return;

    let parsedMetadata: Record<string, any> = {};
    try {
      const rawMetadata = form.metadata.trim() || '{}';
      const parsed = JSON.parse(rawMetadata);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        toast.error('Metadata must be a valid JSON object');
        return;
      }
      parsedMetadata = parsed as Record<string, any>;
    } catch {
      toast.error('Metadata must be valid JSON');
      return;
    }

    try {
      setSaving(true);
      const payload: CreateMcpServerPayload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        transportType: form.transportType,
        endpoint: form.endpoint.trim() || undefined,
        authScheme: form.authScheme.trim() || undefined,
        status: form.status,
        isActive: form.isActive,
        metadata: parsedMetadata,
      };

      if (editing?.id) {
        await mcpService.updateMasterServer(editing.id, payload);
        toast.success('MCP master updated');
      } else {
        await mcpService.createMasterServer(payload);
        toast.success('MCP master created');
      }

      setOpenModal(false);
      setEditing(null);
      resetForm();
      await load();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to save MCP master');
    } finally {
      setSaving(false);
    }
  };

  const runValidate = async (server: McpMasterServer) => {
    const authScheme = String(server.authScheme || '').toLowerCase();
    if (authScheme === 'oauth2') {
      toast.info(
        'OAuth2 integrations require authorization from Agent Integrations. Use Authorize there before validate/discover.'
      );
      setServerMessage(
        server.id,
        'OAuth required: authorize this integration from an agent connection first.'
      );
      return;
    }

    setActionLoading((current) => ({ ...current, [server.id]: 'validate' }));
    setServerMessage(server.id, '');
    try {
      const result = await mcpService.validateMasterServer(server.id, {});
      const message = `Validated (${result?.latencyMs ?? '-'}ms, tools: ${result?.toolCount ?? 0})`;
      setServerMessage(server.id, message);
      toast.success(`${server.name}: validation succeeded`);
    } catch (e: any) {
      const message = e?.message || 'Validation failed';
      setServerMessage(server.id, `Validation error: ${message}`);
      toast.error(`${server.name}: ${message}`);
    } finally {
      setActionLoading((current) => ({ ...current, [server.id]: '' }));
    }
  };

  const runDiscover = async (server: McpMasterServer) => {
    const authScheme = String(server.authScheme || '').toLowerCase();
    const isAdapterHttp = String(server.transportType || '').toLowerCase() === 'adapter_http';
    if (authScheme === 'oauth2' && !isAdapterHttp) {
      toast.info(
        'OAuth2 integrations require authorization from Agent Integrations. Use Authorize there before validate/discover.'
      );
      setServerMessage(
        server.id,
        'OAuth required: authorize this integration from an agent connection first.'
      );
      return;
    }

    setActionLoading((current) => ({ ...current, [server.id]: 'discover' }));
    setServerMessage(server.id, '');
    try {
      const result = await mcpService.discoverMasterServerTools(server.id, {});
      const synced = Number(result?.syncedCount || 0);
      setServerMessage(server.id, `Discovery synced ${synced} tools`);
      setToolCountByServer((current) => ({ ...current, [server.id]: synced }));
      toast.success(`${server.name}: discovery synced ${synced} tools`);
      await load();
    } catch (e: any) {
      const message = e?.message || 'Discovery failed';
      setServerMessage(server.id, `Discovery error: ${message}`);
      toast.error(`${server.name}: ${message}`);
    } finally {
      setActionLoading((current) => ({ ...current, [server.id]: '' }));
    }
  };

  const toggleActive = async (server: McpMasterServer) => {
    try {
      await mcpService.updateMasterServer(server.id, {
        isActive: !server.isActive,
      });
      toast.success(`${server.name} ${server.isActive ? 'disabled' : 'enabled'}`);
      await load();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to update status');
    }
  };

  const filtered = useMemo(() => items, [items]);

  if (role !== UserRoleEnums.SUPER_ADMIN) {
    return (
      <Page contentClassName="py-8">
        <Card className="max-w-4xl">
          <h1 className="text-xl font-semibold">Unauthorized</h1>
          <p className="mt-1 text-agentic-text-secondary">This screen is available only to Super Admin users.</p>
        </Card>
      </Page>
    );
  }

  return (
    <Page contentClassName="py-6">
      <PageHeader
        className="py-0"
        title="MCP Master Catalog"
        subtitle="Manage platform MCP masters. Validate connectivity and discover tools before publishing to agent types."
        aside={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={load} disabled={loading}>Refresh</Button>
            <Button onClick={openCreate}>+ New MCP Master</Button>
          </div>
        }
      />

      <Section className="py-6">
        <Card className="mb-6">
          <div className="grid gap-3 md:grid-cols-[1fr_220px]">
            <Input
              type="text"
              placeholder="Search by code or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <SelectField
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-11 rounded-xl border border-agentic-gray-300 px-3 text-agentic-text-primary"
            >
              <option value="all">All statuses</option>
              {MCP_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </SelectField>
          </div>
        </Card>

        {loading ? (
          <div className="py-12 text-center text-agentic-text-secondary">Loading MCP masters...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-agentic-text-secondary">No MCP masters found.</div>
        ) : (
          <Card className="overflow-x-auto p-0">
            <Table className="w-full min-w-[1160px]">
              <thead className="bg-agentic-gray-100 text-left text-sm text-agentic-text-secondary">
                <tr>
                  <th className="p-4">Server</th>
                  <th className="p-4">Transport</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Tools</th>
                  <th className="p-4">Last action</th>
                  <th className="p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((server) => {
                  const currentAction = actionLoading[server.id] || '';
                  const isBusy = Boolean(currentAction);
                  const isOauthServer = String(server.authScheme || '').toLowerCase() === 'oauth2';
                  const isAdapterHttp = String(server.transportType || '').toLowerCase() === 'adapter_http';
                  const isDiscoverDisabled = isOauthServer && !isAdapterHttp;
                  const toolCount = toolCountByServer[server.id];
                  const lastMessage = lastMessageByServer[server.id];
                  return (
                    <tr key={server.id} className="border-t border-agentic-gray-200 hover:bg-agentic-gray-50">
                      <td className="p-4">
                        <p className="font-semibold text-agentic-text-primary">{server.name}</p>
                        <p className="text-sm text-agentic-text-secondary">{server.code}</p>
                        {server.description ? (
                          <p className="mt-1 text-xs text-agentic-text-secondary">{server.description}</p>
                        ) : null}
                      </td>
                      <td className="p-4">
                        <p className="text-sm font-medium text-agentic-text-primary">
                          {MCP_TRANSPORT_LABELS[server.transportType] || server.transportType}
                        </p>
                        <p className="text-xs text-agentic-text-secondary">{server.authScheme || 'no-auth'}</p>
                      </td>
                      <td className="p-4">
                        <span className={`rounded px-2 py-1 text-xs ${server.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {server.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <p className="mt-1 text-xs uppercase text-agentic-text-secondary">{server.status}</p>
                      </td>
                      <td className="p-4 text-sm text-agentic-text-primary">
                        {toolCount === undefined ? '...' : toolCount}
                      </td>
                      <td className="p-4 text-xs text-agentic-text-secondary">
                        {lastMessage || '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <Button size="sm" variant="secondary" onClick={() => openEdit(server)}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => runValidate(server)}
                            loading={currentAction === 'validate'}
                            disabled={isBusy || isOauthServer}
                            title={isOauthServer ? 'Authorize from Agent Integrations first' : undefined}
                          >
                            Validate
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => runDiscover(server)}
                            loading={currentAction === 'discover'}
                            disabled={isBusy || isDiscoverDisabled}
                            title={isDiscoverDisabled ? 'Authorize from Agent Integrations first' : undefined}
                          >
                            Discover
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => toggleActive(server)}>
                            {server.isActive ? 'Disable' : 'Enable'}
                          </Button>
                        </div>
                        {isOauthServer ? (
                          <p className="mt-2 text-xs text-agentic-text-secondary">
                            OAuth2 server: authorize from Agent Integrations to enable validation/discovery.
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        )}
      </Section>

      <Modal
        isOpen={openModal}
        onClose={() => !saving && setOpenModal(false)}
        title={editing ? 'Edit MCP Master' : 'Create MCP Master'}
        size="lg"
        bodyClassName="pb-10"
      >
        <form onSubmit={saveServer} className="space-y-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            This form creates a platform-level MCP master template. Client credentials are not entered here.
            Clients connect later from their agent using their own credentials.
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Code *"
              value={form.code}
              readOnly
              disabled
              placeholder="Auto-generated from Name"
              helperText={editing ? 'Code is immutable after creation.' : 'Auto-generated from Name.'}
            />
            <Input label="Name *" value={form.name} onChange={(e) => updateName(e.target.value)} />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-agentic-text-primary">Description</label>
            <TextArea tone="agentic"
              value={form.description}
              onChange={(e) => updateForm({ description: e.target.value })}
              rows={3}
              className="w-full rounded-xl border px-4 py-3"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-agentic-text-primary">Transport</label>
              <SelectField tone="agentic"
                value={form.transportType}
                onChange={(e) => updateForm({ transportType: e.target.value })}
                className="h-11 w-full rounded-xl border px-3"
              >
                {MCP_TRANSPORTS.map((transport) => (
                  <option key={transport} value={transport}>
                    {MCP_TRANSPORT_LABELS[transport] || transport}
                  </option>
                ))}
              </SelectField>
              <p className="mt-1 text-xs text-agentic-text-secondary">
                Select how the platform reaches this MCP provider.
              </p>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-agentic-text-primary">Auth Scheme</label>
              <SelectField tone="agentic"
                value={selectedAuthScheme}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    updateForm({ authScheme: form.authScheme && !MCP_AUTH_SCHEMES.includes(form.authScheme.toLowerCase()) ? form.authScheme : '' });
                    return;
                  }
                  updateForm({ authScheme: value });
                }}
                className="h-11 w-full rounded-xl border px-3"
              >
                {MCP_AUTH_SCHEMES.map((scheme) => (
                  <option key={scheme} value={scheme}>
                    {MCP_AUTH_LABELS[scheme] || scheme}
                  </option>
                ))}
              </SelectField>
              <p className="mt-1 text-xs text-agentic-text-secondary">
                {isStdioTransport
                  ? 'Usually No Authentication for STDIO integrations.'
                  : 'Credential values are provided by clients during connection.'}
              </p>
            </div>
          </div>

          {selectedAuthScheme === 'custom' ? (
            <Input
              label="Custom Auth Scheme"
              value={form.authScheme}
              onChange={(e) => updateForm({ authScheme: e.target.value })}
              placeholder="e.g. signed_jwt"
              helperText="Use only if the integration needs a non-standard auth scheme."
            />
          ) : null}

          {!isStdioTransport ? (
            <Input
              label={endpointLabel}
              value={form.endpoint}
              onChange={(e) => updateForm({ endpoint: e.target.value })}
              placeholder={endpointPlaceholder}
              helperText={endpointHelper}
            />
          ) : (
            <div className="rounded-lg border border-agentic-gray-200 bg-agentic-gray-50 px-3 py-2 text-xs text-agentic-text-secondary">
              Endpoint is not required for `stdio` transport.
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-agentic-text-primary">Metadata (JSON)</label>
            <TextArea tone="agentic"
              value={form.metadata}
              onChange={(e) => updateForm({ metadata: e.target.value })}
              rows={5}
              placeholder={'{\n  "adapterProvider": "hubspot"\n}'}
              className="w-full rounded-xl border px-4 py-3 font-mono text-sm"
            />
            <p className="mt-1 text-xs text-agentic-text-secondary">
              Adapter example: <code>{'{"adapterProvider":"hubspot"}'}</code>. For OAuth2 with platform credentials (end users only click Authorize), add{' '}
              <code>oauthClientId</code> and <code>oauthClientSecret</code> (super admin only; not sent to agents).
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-agentic-text-primary">Status</label>
              <SelectField tone="agentic"
                value={form.status}
                onChange={(e) => updateForm({ status: e.target.value })}
                className="h-11 w-full rounded-xl border px-3"
              >
                {MCP_STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </SelectField>
              <p className="mt-1 text-xs text-agentic-text-secondary">
                Use `approved` only after discovery returns tools.
              </p>
            </div>

            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm text-agentic-text-primary">
                <Checkbox
                 
                  checked={form.isActive}
                  onChange={(e) => updateForm({ isActive: e.target.checked })}
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm text-agentic-text-primary">
                <Checkbox
                 
                  checked={form.allowApproveWithoutTools}
                  onChange={(e) => updateForm({ allowApproveWithoutTools: e.target.checked })}
                />
                Allow approved with zero tools (override)
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pb-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpenModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" loading={saving} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create MCP Master'}
            </Button>
          </div>
        </form>
      </Modal>
    </Page>
  );
};

export default McpCatalogPage;
